// cleanup_remove_external_libraries_PAYMENT_MAPS
// cleanup_convert_to_typescript_file_PAYMENT_MAPS

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { pick } from 'lodash';

import { ValidatedInput as Input, BareCheckboxRadio as Checkbox } from '@glu/form-components';

import useLocale from 'components/useLocale';
import { DEFAULT_PROPS } from 'constants.js';

import useStyles from './Header.styles';
import MapNameInput from './MapName';
import DelimitedDropdown from './DelimitedDropdown';
import { delimitedDropdownValidation, asyncExecuteMultiplyGluFunctions } from './utils';
import {
  FIXED_FORMAT_HEADER_FIELDS,
  getFixedFormatTableFields,
  MAP_FORMAT,
  RECORD_LENGTH_FIELD_SETTINGS,
  recordLengthValidators
} from './utils.mapFormats';
import {
  DEFAULT_DELIMITER_OPTION,
  DEFAULT_END_OF_RECORD_OPTION,
  HEADER_FIELDS
} from './constants';

const { EMPTY_ARRAY, EMPTY_FUNCTION, EMPTY_OBJ } = DEFAULT_PROPS;

export const HeaderComponent = ({
  achDelimitersData = EMPTY_ARRAY,
  endOfRecordsData = EMPTY_ARRAY,
  formState = EMPTY_OBJ,
  initialData = EMPTY_OBJ,
  mapFormat = null,
  onChange = EMPTY_FUNCTION,
  sending = false,
  validate = EMPTY_FUNCTION
}) => {
  const classes = useStyles();
  const locale = useLocale();
  const endOfRecordMethodValidators = useMemo(
    () => delimitedDropdownValidation(locale, 'sameEndOfRecordError'),
    [locale]
  );
  const delimiterValidators = useMemo(
    () => delimitedDropdownValidation(locale, 'sameDelimiterError'),
    [locale]
  );

  const recordLengthValidatorsMemoized = useMemo(() => recordLengthValidators(locale), [locale]);

  const handleRecordLengthBlur = useCallback(() => {
    const tableFields = getFixedFormatTableFields(locale, true, true);

    const pickFieldsData = pick(formState, tableFields);

    const arrayOfFieldNames = Object.keys(pickFieldsData)
      .map((key) => (pickFieldsData[key] ? key : null))
      .filter(Boolean);

    return asyncExecuteMultiplyGluFunctions(arrayOfFieldNames, validate);
  }, [formState, locale, validate]);

  return (
    <div className={classes.wrapper}>
      <MapNameInput
        dataQa="map-name"
        disabled={sending}
        initialData={initialData}
        label={locale['wireImports.mapName']}
        maxLengthIndicatorLimit={40}
        name={HEADER_FIELDS.MAP_NAME}
      />
      {mapFormat === MAP_FORMAT.DELIMITED ? (
        <>
          <DelimitedDropdown
            dataQa="delimiter"
            {...(!formState[HEADER_FIELDS.DELIMITER]
              ? { defaultValue: DEFAULT_DELIMITER_OPTION }
              : {})}
            className={classnames(classes.dropdown, classes.delimiter)}
            disabled={sending}
            labelText={locale['wireImports.delimiter']}
            name={HEADER_FIELDS.DELIMITER}
            onChange={() => validate(HEADER_FIELDS.END_OF_RECORD_METHOD)}
            options={achDelimitersData}
            validators={delimiterValidators}
          />
          <DelimitedDropdown
            dataQa="endOfRecord"
            {...(!formState[HEADER_FIELDS.END_OF_RECORD_METHOD]
              ? { defaultValue: DEFAULT_END_OF_RECORD_OPTION }
              : {})}
            className={classnames(classes.dropdown, classes.endOfRecord)}
            disabled={sending}
            labelText={locale['wireImports.endOfRecord']}
            name={HEADER_FIELDS.END_OF_RECORD_METHOD}
            onChange={() => validate(HEADER_FIELDS.DELIMITER)}
            options={endOfRecordsData}
            validators={endOfRecordMethodValidators}
          />
        </>
      ) : (
        <Input
          className="form-group"
          data-qa="recordLength"
          disabled={sending}
          labelText={locale['wireImports.recordLength']}
          maxLengthIndicatorLimit={RECORD_LENGTH_FIELD_SETTINGS.MAX_LENGTH}
          min="1"
          name={FIXED_FORMAT_HEADER_FIELDS.RECORD_LENGTH}
          onBlur={handleRecordLengthBlur}
          type="number"
          validators={recordLengthValidatorsMemoized}
        />
      )}
      <div className={classes.extraHeaderOptions}>
        <Checkbox
          checked={formState[HEADER_FIELDS.SKIP_FIRST_RECORD]}
          className={classes.tableCheckbox}
          data-qa="skipFirstHeaderRecords"
          disabled={sending}
          htmlId={HEADER_FIELDS.SKIP_FIRST_RECORD}
          labelText={locale['wireImports.skipFirstHeaderRecords']}
          name={HEADER_FIELDS.SKIP_FIRST_RECORD}
          onChange={onChange}
        />
        {mapFormat === MAP_FORMAT.DELIMITED && (
          <Checkbox
            checked={formState[HEADER_FIELDS.STRIP_QUOTES]}
            className={classes.tableCheckbox}
            data-qa="stripQuotesAroundFields"
            disabled={sending}
            htmlId={HEADER_FIELDS.STRIP_QUOTES}
            labelText={locale['wireImports.stripQuotesAroundFields']}
            name={HEADER_FIELDS.STRIP_QUOTES}
            onChange={onChange}
          />
        )}
        <Checkbox
          checked={formState[HEADER_FIELDS.ZERO_AMOUNT_FIELDS]}
          className={classes.tableCheckbox}
          data-qa="LoadZero"
          disabled={sending}
          htmlId={HEADER_FIELDS.ZERO_AMOUNT_FIELDS}
          labelText={locale['wireImports.LoadZero']}
          name={HEADER_FIELDS.ZERO_AMOUNT_FIELDS}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

HeaderComponent.propTypes = {
  formState: PropTypes.shape({}),
  initialData: PropTypes.shape({}),
  mapFormat: PropTypes.string,
  onChange: PropTypes.func,
  validate: PropTypes.func,
  sending: PropTypes.bool,
  achDelimitersData: PropTypes.arrayOf(PropTypes.shape({})),
  endOfRecordsData: PropTypes.arrayOf(PropTypes.shape({}))
};

export default HeaderComponent;
